import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { ApiOk } from "@/app/types/api";
import { mapServiceBookingDetailResponse } from "@/app/_store/api/mappers/serviceMappers";

type LegacySeriesBookingCreateInput = {
  Customer_ID: number;
  Animal_ID: number[];
  CourseSeries_ID: number;
  CommentB?: string | null;
};

type ServiceSeriesBookingCreateInput = {
  offerId: number;
  seriesId: number;
  customerId: number;
  dogIds: number[];
  entitlementSelectionByDog?: Record<number, number | null>;
  comment?: string | null;
};

export const serviceSeriesBookingsApi = createApi({
  reducerPath: "serviceSeriesBookingsApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api/service-bookings" }),
  tagTypes: ["ServiceSeriesBookings"],
  endpoints: (builder) => ({
    getAllServiceSeriesBookings: builder.query<any[], void>({
      query: () => ({ url: "", method: "GET" }),
      transformResponse: (response: ApiOk<any[]>) =>
        Array.isArray(response?.data) ? response.data : [],
      providesTags: [{ type: "ServiceSeriesBookings", id: "LIST" }],
    }),
    getServiceSeriesBookingById: builder.query<any | null, string | number>({
      query: (id) => ({ url: `/${id}`, method: "GET" }),
      transformResponse: (response: ApiOk<any>) =>
        mapServiceBookingDetailResponse(response?.data),
      providesTags: (_result, _error, id) => [
        { type: "ServiceSeriesBookings", id: String(id) },
      ],
    }),
    createServiceSeriesBooking: builder.mutation<
      any,
      LegacySeriesBookingCreateInput | ServiceSeriesBookingCreateInput
    >({
      query: (body) => ({
        url: "/series",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiOk<any>) => response?.data,
      invalidatesTags: [{ type: "ServiceSeriesBookings", id: "LIST" }],
    }),
    updateServiceSeriesBooking: builder.mutation<
      { id: number },
      { id: string | number; data: { CommentB?: string | null } }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (response: ApiOk<{ id: number }>) => response?.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ServiceSeriesBookings", id: "LIST" },
        { type: "ServiceSeriesBookings", id: String(id) },
      ],
    }),
    deleteServiceSeriesBooking: builder.mutation<{ success: boolean }, string | number>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiOk<{ success: boolean }>) => response?.data,
      invalidatesTags: (_result, _error, id) => [
        { type: "ServiceSeriesBookings", id: "LIST" },
        { type: "ServiceSeriesBookings", id: String(id) },
      ],
    }),
    restoreServiceSeriesBooking: builder.mutation<any, string | number>({
      query: (id) => ({
        url: `/${id}/restore`,
        method: "POST",
      }),
      transformResponse: (response: ApiOk<any>) => response?.data,
      invalidatesTags: (_result, _error, id) => [
        { type: "ServiceSeriesBookings", id: "LIST" },
        { type: "ServiceSeriesBookings", id: String(id) },
      ],
    }),
  }),
});

export const {
  useGetAllServiceSeriesBookingsQuery,
  useGetServiceSeriesBookingByIdQuery,
  useCreateServiceSeriesBookingMutation,
  useUpdateServiceSeriesBookingMutation,
  useDeleteServiceSeriesBookingMutation,
  useRestoreServiceSeriesBookingMutation,
} = serviceSeriesBookingsApi;
